import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  Snackbar,
  Alert,
  createTheme,
  CircularProgress,
  Tooltip as MuiTooltip
} from '@mui/material';
import { useAuthToken } from "../TokenContext";

import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PercentIcon from '@mui/icons-material/Percent';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import { useSelector } from 'react-redux';
import './controlplane/ArtemyStyles.css';

// Styled components revamped for Artemy Look
const DashboardWrapper = styled('div')`
  background-color: var(--artemy-bg-base);
  min-height: 100vh;
  width: 100%;
  color: var(--artemy-text-primary);
  font-family: var(--artemy-font-body);
  position: relative;
`;

const ContentContainer = styled('div')`
  animation: artemy-fade-up 0.4s ease-out both;
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHeader = styled('div')`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const MetricCard = styled('div')`
  background: var(--artemy-bg-card);
  border: 1px solid var(--artemy-border);
  border-radius: 14px;
  padding: 24px;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    border-color: var(--artemy-accent);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(61, 127, 255, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.color || 'var(--artemy-accent)'};
    opacity: 0.8;
  }
`;

const IconWrapper = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bgColor || 'rgba(61, 127, 255, 0.1)'};
  color: ${props => props.color || 'var(--artemy-accent)'};
  margin-bottom: 16px;
`;

const GlassCard = styled(Card)`
  background: rgba(17, 20, 32, 0.6) !important;
  backdrop-filter: blur(12px);
  border: 1px solid var(--artemy-border) !important;
  border-radius: 16px !important;
  padding: 24px;
  color: var(--artemy-text-primary) !important;
  box-shadow: none !important;
`;

const OpportunityColumn = styled('div')`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--artemy-border);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
`;

const OpportunityItem = styled('div')`
  background: var(--artemy-bg-elevated);
  border: 1px solid var(--artemy-border);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--artemy-border-bright);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TableWrapper = styled('div')`
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--artemy-border);
  border-radius: 12px;
  background: var(--artemy-bg-elevated);

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--artemy-border-bright);
    border-radius: 10px;
  }
`;

const StyledTable = styled('table')`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled('th')`
  text-align: left;
  padding: 14px 16px;
  color: var(--artemy-text-secondary);
  font-family: var(--artemy-font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid var(--artemy-border);
  background: rgba(255, 255, 255, 0.02);
`;

const Td = styled('td')`
  padding: 14px 16px;
  color: var(--artemy-text-primary);
  border-bottom: 1px solid var(--artemy-border);
`;

const StatusBadge = styled('span')`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'approved': return 'rgba(0, 212, 170, 0.15)';
      case 'pending': return 'rgba(245, 166, 35, 0.15)';
      case 'under review': return 'rgba(167, 139, 250, 0.15)';
      default: return 'rgba(139, 150, 180, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.status.toLowerCase()) {
      case 'approved': return 'var(--artemy-green)';
      case 'pending': return 'var(--artemy-yellow)';
      case 'under review': return 'var(--artemy-purple)';
      default: return 'var(--artemy-text-secondary)';
    }
  }};
`;

// Opportunity data
const opportunityData = {
  previousWeek: {
    new: [
      { id: "OPP-001", clientName: "Tech Corp", requirement: "Senior React Developer", probability: "75%", status: "new", nextAction: "Technical Interview", dueDate: "2024-12-20", value: 150000, location: "Remote" },
      { id: "OPP-002", clientName: "Digital Solutions", requirement: "Full Stack Developer", probability: "60%", status: "new", nextAction: "Initial Discussion", dueDate: "2024-12-21", value: 120000, location: "Hybrid" }
    ],
    inProgress: [
      { id: "OPP-003", clientName: "Innovation Labs", requirement: "DevOps Engineer", probability: "85%", status: "inProgress", nextAction: "Final Interview", dueDate: "2024-12-18", value: 180000, location: "Remote" }
    ],
    followUps: [
      { id: "OPP-004", clientName: "Cloud Systems", requirement: "Cloud Architect", probability: "90%", status: "followUp", nextAction: "Contract Review", dueDate: "2024-12-19", value: 200000, location: "Onsite" }
    ]
  },
  currentWeek: {
    new: [
      { id: "OPP-005", clientName: "Data Analytics Co", requirement: "Data Engineer", probability: "65%", status: "new", nextAction: "Skills Assessment", dueDate: "2024-12-24", value: 160000, location: "Remote" }
    ],
    inProgress: [
      { id: "OPP-006", clientName: "Health Systems", requirement: "System Architect", probability: "95%", status: "inProgress", nextAction: "Final Approval", dueDate: "2024-12-22", value: 220000, location: "Remote" }
    ],
    followUps: [
      { id: "OPP-007", clientName: "Retail Solutions", requirement: "QA Engineer", probability: "75%", status: "followUp", nextAction: "Test Plan Review", dueDate: "2024-12-20", value: 110000, location: "Remote" }
    ]
  }
};

const PartnerMetricsDashboard = ({ collapsed }) => {
  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();

  const [metricsData, setMetricsData] = useState(null);
  const [burData, setBurData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchAllData();
  }, [token]);

  const transformBurDataForChart = () => {
    if (!burData || burData.length === 0) {
      return [
        { month: 'Jan 2024', rate: 65 },
        { month: 'Feb 2024', rate: 72 },
        { month: 'Mar 2024', rate: 68 },
        { month: 'Apr 2024', rate: 84 },
        { month: 'May 2024', rate: 81 },
        { month: 'Jun 2024', rate: 88 }
      ];
    }
    const monthlyData = {};
    burData.forEach(item => {
      if (item.currency !== '' && item.ym) {
        if (!monthlyData[item.ym]) {
          monthlyData[item.ym] = { month: item.ym, totalBilling: 0, totalRevenue: 0 };
        }
        monthlyData[item.ym].totalBilling += item.total_gross_billing || 0;
        monthlyData[item.ym].totalRevenue += item.total_platform_revenue || 0;
      }
    });
    return Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6)
      .map(item => {
        const [year, month] = item.month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return {
          month: `${monthNames[parseInt(month) - 1]} ${year}`,
          rate: item.totalBilling > 0 ? Math.round((item.totalRevenue / item.totalBilling) * 100) : 0,
        };
      });
  };

  const transformRevenueDataForChart = () => {
    if (!revenueData || revenueData.length === 0) {
      return [
        { name: 'Pod α', revenue: 45000 },
        { name: 'Pod β', revenue: 32000 },
        { name: 'Pod γ', revenue: 68000 },
        { name: 'Pod δ', revenue: 25000 },
        { name: 'Pod ε', revenue: 51000 }
      ];
    }
    const podData = revenueData.filter(item => item.podID !== 'ALL_PODS_TOTAL' && item.total_platform_revenue > 0);
    const conversionRates = { 'INR': 0.012, 'USD': 1, 'Vietnamese Dong': 0.00004 };
    const podRevenueMap = {};
    podData.forEach(item => {
      if (!podRevenueMap[item.podID]) podRevenueMap[item.podID] = { podID: item.podID, revenueUSD: 0 };
      const rate = conversionRates[item.currency] || 1;
      podRevenueMap[item.podID].revenueUSD += (item.total_platform_revenue * rate);
    });
    return Object.values(podRevenueMap).map(pod => ({
      name: `Pod ${pod.podID}`,
      revenue: Math.round(pod.revenueUSD),
    }));
  };

  const hasRealData = burData && burData.length > 0 && revenueData && revenueData.length > 0;

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([fetchMetricsData(), fetchBurData(), fetchRevenueForecastData()]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load real-time metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsData = async () => {
    try {
      const response = await fetch('https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/partnertalentCountmetrics', {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ "partner_code": user?.data[0]?.client_code }),
      });
      const result = await response.json();
      if (result.status === 200) setMetricsData(result.data);
    } catch (err) { console.error(err); }
  };

  const fetchBurData = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_PARTNERMETRICSBUR, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ "partner_code": user?.data[0]?.client_code }),
      });
      const result = await response.json();
      if (result.statusCode === 200) setBurData(result.body);
    } catch (err) { console.error(err); }
  };

  const fetchRevenueForecastData = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_PARTNERMETRICSREVENUEFORCAST, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ "partner_code": user?.data[0]?.client_code }),
      });
      const result = await response.json();
      if (result.statusCode === 200) setRevenueData(result.body);
    } catch (err) { console.error(err); }
  };

  const calculateBillablePercentage = () => {
    if (!burData || burData.length === 0) return 84;
    const validEntries = burData.filter(item => item.ym);
    if (validEntries.length === 0) return 84;
    const latestMonth = validEntries.reduce((latest, item) => item.ym > latest ? item.ym : latest, '');
    const currentMonthData = validEntries.filter(item => item.ym === latestMonth);
    const totalBilling = currentMonthData.reduce((sum, item) => sum + (item.total_gross_billing || 0), 0);
    const totalRevenue = currentMonthData.reduce((sum, item) => sum + (item.total_platform_revenue || 0), 0);
    return totalBilling === 0 ? 0 : Math.round((totalRevenue / totalBilling) * 100);
  };

  const calculateRevenueForecast = () => {
    if (!revenueData || revenueData.length === 0) return 185000;
    return Math.round(revenueData.reduce((sum, item) => item.podID === 'ALL_PODS_TOTAL' ? sum + (item.total_platform_revenue || 0) : sum, 0));
  };

  const handleRefresh = async () => {
    await fetchAllData();
    setSnackbar({ open: true, message: 'Dashboard updated with latest data', severity: 'success' });
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const quoteData = [
    { customerName: "Tech Solutions Inc", quoteDate: "2024-12-10", description: "Full Stack Development Team", budget: "$250,000", status: "Pending", amount: "$245,000" },
    { customerName: "Global Systems Corp", quoteDate: "2024-12-11", description: "DevOps Implementation", budget: "$180,000", status: "Approved", amount: "$175,000" },
    { customerName: "DataFlow Analytics", quoteDate: "2024-12-12", description: "Data Engineering Team", budget: "$200,000", status: "Under Review", amount: "$198,000" },
    { customerName: "Cloud Innovate", quoteDate: "2024-12-13", description: "Cloud Architecture Team", budget: "$300,000", status: "Pending", amount: "$295,000" }
  ];

  const renderOpportunities = (data, title) => (
    <Box mb={4}>
      <Typography variant="overline" sx={{ color: 'var(--artemy-text-dim)', fontWeight: 700, letterSpacing: 1 }}>{title}</Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {['new', 'inProgress', 'followUps'].map((key) => (
          <Grid item xs={12} md={4} key={key}>
            <OpportunityColumn>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'var(--artemy-text-secondary)', textTransform: 'capitalize' }}>
                {key.replace(/([A-Z])/g, ' $1')} ({data[key].length})
              </Typography>
              {data[key].map(opp => (
                <OpportunityItem key={opp.id}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2" sx={{ color: 'var(--artemy-accent)' }}>{opp.clientName}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--artemy-text-dim)' }}>{opp.probability}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '12px', my: 0.5 }}>{opp.requirement}</Typography>
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" sx={{ color: 'var(--artemy-text-dim)' }}>{opp.location}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--artemy-green)' }}>{opp.nextAction}</Typography>
                  </Box>
                </OpportunityItem>
              ))}
            </OpportunityColumn>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <DashboardWrapper className="artemy-root">
      <ContentContainer>
        <SectionHeader>
          <div>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography className="artemy-page-title">Partner Intelligence Dashboard</Typography>
              {!hasRealData && !loading && (
                <Box sx={{ 
                  px: 1, py: 0.25, borderRadius: 1, border: '1px solid var(--artemy-yellow)',
                  color: 'var(--artemy-yellow)', fontSize: 10, fontWeight: 700, fontFamily: 'var(--artemy-font-mono)'
                }}>DEMO DATA</Box>
              )}
            </Box>
            <Typography className="artemy-page-sub">Comprehensive metrics and opportunity tracking for distribution partners</Typography>
          </div>
          <Button 
            className="artemy-btn artemy-btn-ghost" 
            onClick={handleRefresh} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          >
            Refresh Intel
          </Button>
        </SectionHeader>

        {/* Global Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard color="var(--artemy-accent)">
              <IconWrapper bgColor="rgba(61, 127, 255, 0.1)" color="var(--artemy-accent)">
                <PercentIcon fontSize="small" />
              </IconWrapper>
              <div>
                <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>Billable Utilization</Typography>
                <Typography variant="h4" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                  {loading ? '...' : `${calculateBillablePercentage()}%`}
                </Typography>
              </div>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard color="var(--artemy-green)">
              <IconWrapper bgColor="rgba(0, 212, 170, 0.1)" color="var(--artemy-green)">
                <GroupIcon fontSize="small" />
              </IconWrapper>
              <div>
                <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>Active Talent Bench</Typography>
                <Typography variant="h4" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                  {loading ? '...' : (metricsData?.bench_count || 18)}
                </Typography>
              </div>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard color="var(--artemy-purple)">
              <IconWrapper bgColor="rgba(167, 139, 250, 0.1)" color="var(--artemy-purple)">
                <AttachMoneyIcon fontSize="small" />
              </IconWrapper>
              <div>
                <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>Forecast Revenue</Typography>
                <Typography variant="h4" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                  {loading ? '...' : `$${calculateRevenueForecast().toLocaleString()}`}
                </Typography>
              </div>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard color="var(--artemy-yellow)">
              <IconWrapper bgColor="rgba(245, 166, 35, 0.1)" color="var(--artemy-yellow)">
                <StarIcon fontSize="small" />
              </IconWrapper>
              <div>
                <Typography variant="caption" sx={{ color: 'var(--artemy-text-secondary)' }}>Talent Quality</Typography>
                <Typography variant="h4" sx={{ fontStyle: 'italic', fontWeight: 600 }}>4.8/5.0</Typography>
              </div>
            </MetricCard>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={6}>
            <GlassCard>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 700, fontFamily: 'var(--artemy-font-display)' }}>Billable Utilization Trend</Typography>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer>
                  <LineChart data={transformBurDataForChart()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10 }} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ background: 'var(--artemy-bg-elevated)', border: '1px solid var(--artemy-border)', borderRadius: '8px' }}
                      itemStyle={{ color: 'var(--artemy-accent)' }}
                    />
                    <Line type="monotone" dataKey="rate" stroke="var(--artemy-accent)" strokeWidth={3} dot={{ r: 4, fill: 'var(--artemy-accent)' }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </GlassCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <GlassCard>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 700, fontFamily: 'var(--artemy-font-display)' }}>Revenue by Pod Structure</Typography>
              <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer>
                  <BarChart data={transformRevenueDataForChart()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10 }} />
                    <Tooltip 
                      cursor={{ fill: 'rgba(255,255,255,0.02)' }}
                      contentStyle={{ background: 'var(--artemy-bg-elevated)', border: '1px solid var(--artemy-border)', borderRadius: '8px' }}
                    />
                    <Bar dataKey="revenue" fill="var(--artemy-green)" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Opportunity Activity */}
        <Box sx={{ mb: 5 }}>
          <Typography className="artemy-page-title" sx={{ mb: 1, fontSize: '18px' }}>Active Opportunities</Typography>
          <Box className="artemy-card" sx={{ p: 4 }}>
            {renderOpportunities(opportunityData.previousWeek, "Timeline: Previous Week")}
            <Box sx={{ my: 4, borderTop: '1px solid var(--artemy-border)' }} />
            {renderOpportunities(opportunityData.currentWeek, "Timeline: Current Week")}
          </Box>
        </Box>

        {/* Quote Requests Table */}
        <Box sx={{ mb: 6 }}>
          <Typography className="artemy-page-title" sx={{ mb: 2, fontSize: '18px' }}>Incoming Quote Requests</Typography>
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Date</Th>
                  <Th>Requirement</Th>
                  <Th>Budget</Th>
                  <Th>Status</Th>
                  <Th>Amount</Th>
                </tr>
              </thead>
              <tbody>
                {quoteData.map((quote, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <Td>{quote.customerName}</Td>
                    <Td>{new Date(quote.quoteDate).toLocaleDateString()}</Td>
                    <Td sx={{ color: 'var(--artemy-text-secondary)' }}>{quote.description}</Td>
                    <Td>{quote.budget}</Td>
                    <Td><StatusBadge status={quote.status}>{quote.status}</StatusBadge></Td>
                    <Td sx={{ fontWeight: 600 }}>{quote.amount}</Td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </Box>
      </ContentContainer>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ background: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-primary)', border: '1px solid var(--artemy-border)' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardWrapper>
  );
};

export default PartnerMetricsDashboard;